/**
 * CoupleCrossword — dedicated mobile game workspace (Phase 4C-2 final).
 * NYT/USA-Today-style: fullscreen game mode (100dvh) with compact game bar,
 * fitted grid, sticky clue bar, in-app crossword keyboard, and a slide-up
 * clue drawer. Server-authoritative timer, autosave, live wrong-letter check,
 * one official attempt, leaderboard — all preserved.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import api, { formatErr } from "../lib/api";
import { useEventSocket } from "../lib/ws";
import { useMotif } from "../contexts/MotifContext";
import { toast } from "sonner";
import { Timer, Send, X, ChevronLeft, ChevronRight, ArrowLeft, List } from "lucide-react";
import { cellKey, fmtSecs } from "./crossword/utils";
import { T } from "../lib/i18n";
import { CrosswordGrid } from "./crossword/CrosswordGrid";
import { CrosswordKeyboard } from "./crossword/CrosswordKeyboard";
import { ResultsCard, ClueList, Leaderboard } from "./crossword/CrosswordPanels";
import { useCrosswordKeys } from "./crossword/useCrosswordKeys";

export default function CoupleCrossword({ onBack, skipConfirm = false }) {
  const { eventId } = useMotif();
  const [cw, setCw] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [grid, setGrid] = useState({});
  const [tick, setTick] = useState(0);
  const [leader, setLeader] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const [dir, setDir] = useState("across");
  const [cellPx, setCellPx] = useState(28);
  const [wrongKeys, setWrongKeys] = useState(() => new Set());
  const [clueTab, setClueTab] = useState("across");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const autosaveTimer = useRef(null);
  const checkTimer = useRef(null);
  const lastAutoSaved = useRef("");
  const cellRefs = useRef({});
  const gridAreaRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: cwData } = await api.get("/crossword/current");
      setCw(cwData);
      if (cwData?.id) {
        const { data: a } = await api.get(`/crossword/${cwData.id}/attempt`);
        setAttempt(a);
        if (a?.grid) setGrid(a.grid || {});
        if (a && !a.submitted_at) setPlaying(true); // resume straight into game mode
      }
    } catch (err) { console.debug("crossword: load failed", err); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cwId = cw?.id;
  const refreshLeaderboard = useCallback(async () => {
    if (!cwId) return;
    try { setLeader((await api.get(`/crossword/${cwId}/leaderboard`)).data); }
    catch (err) { console.debug("crossword: leaderboard refresh failed", err); }
  }, [cwId]);
  useEffect(() => { refreshLeaderboard(); }, [refreshLeaderboard]);
  useEventSocket(eventId, (msg) => { if (msg.type === "crossword_completed") refreshLeaderboard(); });

  useEffect(() => {
    if (!attempt?.started_at || attempt?.completed_at) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [attempt?.started_at, attempt?.completed_at]);

  const elapsedSecs = useMemo(() => {
    if (!attempt?.started_at) return 0;
    if (attempt.completed_at && attempt.elapsed_seconds != null) return attempt.elapsed_seconds;
    return Math.floor((Date.now() - new Date(attempt.started_at).getTime()) / 1000);
    // `tick` intentionally forces a per-second recompute of the live timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, tick]);

  const entries = useMemo(() => {
    if (!cw) return [];
    const mk = (c, d) => ({
      ...c, dir: d,
      cells: Array.from({ length: c.length }, (_, i) =>
        d === "across" ? cellKey(c.row, c.col + i) : cellKey(c.row + i, c.col)),
    });
    return [
      ...(cw.clues_across || []).map((c) => mk(c, "across")),
      ...(cw.clues_down || []).map((c) => mk(c, "down")),
    ];
  }, [cw]);

  const entriesByCell = useMemo(() => {
    const m = {};
    entries.forEach((e) => e.cells.forEach((k) => { m[k] = m[k] || {}; m[k][e.dir] = e; }));
    return m;
  }, [entries]);

  const activeEntry = useMemo(() => {
    if (!sel) return null;
    const at = entriesByCell[cellKey(sel.r, sel.c)];
    if (!at) return null;
    return at[dir] || at[dir === "across" ? "down" : "across"] || null;
  }, [sel, dir, entriesByCell]);

  const activeCells = useMemo(() => new Set(activeEntry ? activeEntry.cells : []), [activeEntry]);

  // Fit grid to the available workspace area (width AND height), floor for tappability
  useEffect(() => {
    if (!playing || !cw) return;
    const el = gridAreaRef.current;
    if (!el) return;
    const compute = () => {
      const w = el.clientWidth - 12;
      const h = el.clientHeight - 12;
      setCellPx(Math.max(24, Math.min(40, Math.floor(Math.min(w / cw.cols, h / cw.rows)))));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [playing, cw]);

  const start = async () => {
    if (!cw) return;
    setStarting(true);
    try {
      const { data } = await api.post(`/crossword/${cw.id}/start`);
      setAttempt(data);
      setGrid(data.grid || {});
      setPlaying(true);
    } catch (e) { toast.error(formatErr(e)); } finally { setStarting(false); }
  };

  const scrollToKey = useCallback((key) => {
    const el = cellRefs.current[key];
    if (el) el.scrollIntoView({ block: "center", inline: "center", behavior: "smooth" });
  }, []);

  const entryFilled = useCallback((e, g = grid) => e.cells.every((k) => g[k]), [grid]);

  const gotoEntry = useCallback((entry, g = grid) => {
    if (!entry) return;
    setDir(entry.dir);
    setClueTab(entry.dir);
    const blank = entry.cells.find((k) => !g[k]) || entry.cells[0];
    const [r, c] = blank.split(",").map(Number);
    setSel({ r, c });
    requestAnimationFrame(() => scrollToKey(blank));
  }, [grid, scrollToKey]);

  const stepEntry = useCallback((delta, g = grid) => {
    if (entries.length === 0) return;
    const idx = activeEntry
      ? entries.findIndex((e) => e.dir === activeEntry.dir && e.number === activeEntry.number)
      : -1;
    let next = idx + delta;
    if (next >= entries.length || idx < 0) {
      const firstUnanswered = entries.find((e) => !entryFilled(e, g));
      gotoEntry(firstUnanswered || entries[0], g);
      return;
    }
    if (next < 0) next = entries.length - 1;
    gotoEntry(entries[next], g);
  }, [entries, activeEntry, gotoEntry, entryFilled, grid]);

  const setCell = useCallback((r, c, letter) => {
    const key = cellKey(r, c);
    let nextGrid;
    setGrid((prev) => {
      nextGrid = { ...prev };
      if (letter) nextGrid[key] = letter;
      else delete nextGrid[key];
      return nextGrid;
    });
    setWrongKeys((prev) => {
      if (!prev.has(key)) return prev;
      const n = new Set(prev);
      n.delete(key);
      return n;
    });
    return nextGrid;
  }, []);

  const completed = !!(attempt?.submitted_at || attempt?.completed_at);

  const typeLetter = useCallback((ch) => {
    if (!sel || !activeEntry || completed) return;
    const nextGrid = setCell(sel.r, sel.c, ch);
    const idx = activeEntry.cells.indexOf(cellKey(sel.r, sel.c));
    if (idx >= 0 && idx < activeEntry.cells.length - 1) {
      const nk = activeEntry.cells[idx + 1];
      const [nr, nc] = nk.split(",").map(Number);
      setSel({ r: nr, c: nc });
      scrollToKey(nk);
    } else if (idx === activeEntry.cells.length - 1) {
      stepEntry(1, nextGrid);
    }
  }, [sel, activeEntry, completed, setCell, scrollToKey, stepEntry]);

  const doBackspace = useCallback(() => {
    if (!sel || !activeEntry || completed) return;
    const key = cellKey(sel.r, sel.c);
    if (grid[key]) { setCell(sel.r, sel.c, ""); return; }
    const idx = activeEntry.cells.indexOf(key);
    if (idx > 0) {
      const pk = activeEntry.cells[idx - 1];
      const [pr, pc] = pk.split(",").map(Number);
      setCell(pr, pc, "");
      setSel({ r: pr, c: pc });
      scrollToKey(pk);
    }
  }, [sel, activeEntry, completed, grid, setCell, scrollToKey]);

  const selectCell = useCallback((r, c) => {
    const key = cellKey(r, c);
    const at = entriesByCell[key];
    if (!at) return;
    if (sel && sel.r === r && sel.c === c) {
      const other = dir === "across" ? "down" : "across";
      if (at[other]) { setDir(other); setClueTab(other); }
    } else {
      setSel({ r, c });
      const d = at[dir] ? dir : (at.across ? "across" : "down");
      setDir(d);
      setClueTab(d);
    }
  }, [entriesByCell, sel, dir]);

  const setDirection = useCallback((d) => { setDir(d); setClueTab(d); }, []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const nextEntry = useCallback(() => stepEntry(1), [stepEntry]);

  // Desktop physical keyboard support while in game mode
  useCrosswordKeys(playing, {
    drawerOpen, closeDrawer, typeLetter, backspace: doBackspace, nextEntry, setDirection,
  });

  // Lock body scroll while game mode is open
  useEffect(() => {
    if (!playing) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [playing]);

  // Autosave (debounced)
  useEffect(() => {
    if (!attempt || attempt.completed_at) return;
    const serialised = JSON.stringify(grid);
    if (serialised === lastAutoSaved.current) return;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      try {
        await api.put(`/crossword/${cw.id}/autosave`, { grid });
        lastAutoSaved.current = serialised;
      } catch (err) { console.debug("crossword: autosave failed", err); }
    }, 800);
    return () => clearTimeout(autosaveTimer.current);
  }, [grid, attempt, cw?.id]);

  // Live wrong-letter check (server-side compare; answers never shipped)
  useEffect(() => {
    if (!attempt || attempt.completed_at || !cw?.id) return;
    if (Object.keys(grid).length === 0) { setWrongKeys(new Set()); return; }
    clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post(`/crossword/${cw.id}/check`, { grid });
        setWrongKeys(new Set(data.wrong || []));
      } catch (err) { console.debug("crossword: live check failed", err); }
    }, 550);
    return () => clearTimeout(checkTimer.current);
  }, [grid, attempt, cw?.id]);

  const submit = async () => {
    if (!cw || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/crossword/${cw.id}/submit`, { grid });
      setAttempt(data);
      setConfirmOpen(false);
      setPlaying(false);
      toast.success(T("Crossword submitted — every correct answer counted!"));
      refreshLeaderboard();
    } catch (e) { toast.error(formatErr(e)); } finally { setSubmitting(false); }
  };

  const clueFilled = useCallback((cl) => {
    const e = entries.find((x) => x.dir === clueTab && x.number === cl.number);
    return e ? entryFilled(e) : false;
  }, [entries, clueTab, entryFilled]);

  const pickClue = useCallback((cl) => {
    const e = entries.find((x) => x.dir === clueTab && x.number === cl.number);
    if (e) gotoEntry(e);
    setDrawerOpen(false);
  }, [entries, clueTab, gotoEntry]);

  if (loading) return <div className="text-center text-motif-muted text-sm py-10">{T("Loading…")}</div>;
  if (!cw) {
    return (
      <div className="text-center text-motif-muted text-sm py-10 space-y-3">
        <div>{T("The Couple Crossword has not been published yet.")}</div>
        {onBack && <button onClick={onBack} className="btn-ghost text-xs" data-testid="crossword-back">{T("Back")}</button>}
      </div>
    );
  }

  // ---------- Fullscreen game workspace ----------
  const workspace = playing && attempt && !completed && createPortal(
    <div
      className="fixed inset-0 z-[80] flex flex-col"
      style={{
        height: "100dvh",
        background: "var(--motif-bg)",
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
      data-testid="crossword-workspace"
      role="dialog"
      aria-label={`${cw.title} game`}
    >
      {/* Game bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-motif flex-shrink-0">
        <button
          onClick={() => { setPlaying(false); onBack?.(); }}
          className="w-9 h-9 rounded-full bg-accent-motif flex items-center justify-center flex-shrink-0"
          aria-label="Back to games (progress saved)"
          data-testid="crossword-exit"
        >
          <ArrowLeft className="w-4 h-4 text-primary-motif" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm text-motif truncate">{cw.title}</div>
          <div className="flex items-center gap-2 text-[11px] text-motif-muted">
            <span className="flex items-center gap-1" data-testid="crossword-timer">
              <Timer className="w-3 h-3 text-primary-motif" /> {fmtSecs(elapsedSecs)}
            </span>
            <span data-testid="crossword-progress">
              Solved: {entries.filter((e) => entryFilled(e)).length} / {entries.length}
            </span>
          </div>
        </div>
        <button
          onClick={() => setDrawerOpen(true)}
          className="flex items-center gap-1 px-3 py-2 rounded-full text-xs bg-accent-motif text-motif flex-shrink-0"
          data-testid="crossword-open-drawer"
          aria-label="Open clue list"
        >
          <List className="w-3.5 h-3.5 text-primary-motif" /> Clues
        </button>
        <button
          onClick={() => setConfirmOpen(true)}
          className="btn-primary px-3 py-2 text-xs flex items-center gap-1 flex-shrink-0"
          data-testid="crossword-submit"
        >
          <Send className="w-3.5 h-3.5" /> Submit Crossword
        </button>
      </div>

      {/* Grid viewport — the only scrollable area */}
      <CrosswordGrid
        cw={cw}
        grid={grid}
        sel={sel}
        activeCells={activeCells}
        wrongKeys={wrongKeys}
        cellPx={cellPx}
        cellRefs={cellRefs}
        gridAreaRef={gridAreaRef}
        onSelect={selectCell}
      />

      {/* Clue bar */}
      <div
        className="flex items-center gap-1 px-2 py-1.5 border-t border-motif flex-shrink-0"
        style={{ background: "var(--motif-accent)" }}
        data-testid="crossword-active-clue"
        aria-live="polite"
      >
        <button onClick={() => stepEntry(-1)} className="w-9 h-9 rounded-full bg-white/60 flex items-center justify-center flex-shrink-0" aria-label="Previous clue" data-testid="crossword-prev-clue">
          <ChevronLeft className="w-4 h-4 text-primary-motif" />
        </button>
        <button className="flex-1 min-w-0 text-left px-1" onClick={() => setDrawerOpen(true)} data-testid="crossword-clue-bar-text" aria-label="Open full clue list">
          {activeEntry ? (
            <span className="text-[13px] text-motif leading-snug line-clamp-2">
              <span className="font-display text-primary-motif mr-1">
                {activeEntry.number} {activeEntry.dir === "across" ? T("Across") : T("Down")} —
              </span>
              <span data-testid="crossword-active-clue-text">{activeEntry.text}</span>
            </span>
          ) : (
            <span className="text-motif-muted text-xs italic">{T("Tap a square to begin.")}</span>
          )}
        </button>
        <button onClick={() => stepEntry(1)} className="w-9 h-9 rounded-full bg-white/60 flex items-center justify-center flex-shrink-0" aria-label="Next clue" data-testid="crossword-next-clue">
          <ChevronRight className="w-4 h-4 text-primary-motif" />
        </button>
      </div>

      {/* In-app crossword keyboard */}
      <CrosswordKeyboard onLetter={typeLetter} onEnter={nextEntry} onBackspace={doBackspace} />

      {/* Submit confirmation — opening never submits by itself */}
      {confirmOpen && (() => {
        const allFilled = entries.length > 0 && entries.every((e) => entryFilled(e));
        return (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 px-4" data-testid="crossword-submit-confirm">
            <div className="card-elev p-5 w-full max-w-sm relative" style={{ background: "var(--motif-surface)" }}>
              <div className="font-display text-xl text-motif mb-2">
                {allFilled ? T("Submit Crossword?") : T("Submit Partial Crossword?")}
              </div>
              <p className="text-sm text-motif-muted mb-4 leading-snug" data-testid="crossword-submit-confirm-msg">
                {allFilled
                  ? T("Your answers will be scored and your final result will be added to the leaderboard. Once submitted, your official attempt is complete.")
                  : T("You still have unanswered clues. All correct completed answers will earn points and your final score will be added to the leaderboard. Once submitted, your official attempt is complete and cannot be reopened.")}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmOpen(false)}
                  className="btn-ghost text-sm flex-1"
                  data-testid="crossword-keep-playing"
                >
                  {allFilled ? T("Review Puzzle") : T("Keep Playing")}
                </button>
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="btn-primary text-sm flex-1"
                  data-testid="crossword-submit-final"
                >
                  {submitting ? T("Submitting…") : T("Submit Final Answers")}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Slide-up clue drawer */}
      {drawerOpen && (
        <div className="absolute inset-0 z-10 flex flex-col justify-end" data-testid="crossword-clue-drawer">
          <button className="flex-1 bg-black/40" onClick={closeDrawer} aria-label="Close clue list" />
          <div className="rounded-t-2xl slide-up flex flex-col" style={{ background: "var(--motif-surface)", maxHeight: "70dvh" }}>
            <div className="flex items-center gap-2 p-3 pb-2 flex-shrink-0">
              <div className="flex gap-2 flex-1">
                {["across", "down"].map((d) => (
                  <button
                    key={d}
                    onClick={() => setClueTab(d)}
                    className="flex-1 px-3 py-1.5 rounded-full text-xs tracking-wide capitalize"
                    style={{
                      background: clueTab === d ? "var(--motif-primary)" : "var(--motif-accent)",
                      color: clueTab === d ? "var(--motif-primary-fg)" : "var(--motif-text-muted)",
                    }}
                    data-testid={`crossword-tab-${d}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
              <button onClick={closeDrawer} className="w-8 h-8 rounded-full bg-accent-motif flex items-center justify-center" aria-label="Close clue list" data-testid="crossword-drawer-close">
                <X className="w-4 h-4 text-motif" />
              </button>
            </div>
            <div className="overflow-y-auto overscroll-contain px-3 pb-4">
              <ClueList
                clues={clueTab === "across" ? cw.clues_across : cw.clues_down}
                dirName={clueTab}
                activeEntry={activeEntry}
                entryFilled={clueFilled}
                onPick={pickClue}
                testid={`crossword-clues-${clueTab}`}
              />
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );

  // ---------- Non-playing (games tab) view ----------
  return (
    <div className="space-y-3 fadein" data-testid="crossword-view">
      <div className="flex items-center gap-2">
        {onBack && <button onClick={onBack} className="btn-ghost text-xs" data-testid="crossword-back">← Back</button>}
        <h2 className="font-display text-xl text-motif flex-1">{cw.title}</h2>
      </div>

      {attempt && !completed && (
        <div className="card-elev p-3 flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Timer className="w-4 h-4 text-primary-motif" />
            <span className="tracking-luxe text-motif-muted text-[11px]">TIME</span>
            <span className="font-display text-lg text-motif">{fmtSecs(elapsedSecs)}</span>
          </div>
        </div>
      )}

      {completed && (
        <ResultsCard attempt={attempt} leader={leader} onBack={onBack} />
      )}

      {!attempt && (
        <div className="card-elev p-4 space-y-3 text-center">
          <div className="text-sm text-motif">
            {T("You only get")} <span className="font-semibold text-primary-motif">{T("one official attempt")}</span>.{" "}
            {T("Your timer starts when you press Begin")}.
          </div>
          <button onClick={start} disabled={starting} className="btn-primary w-full" data-testid="crossword-begin-btn">
            {starting ? T("Starting…") : T("Begin Crossword")}
          </button>
        </div>
      )}

      {attempt && !completed && !playing && (
        <button onClick={() => setPlaying(true)} className="btn-primary w-full" data-testid="crossword-resume-btn">
          {T("Resume Puzzle")}
        </button>
      )}

      <Leaderboard rows={leader} />
      {workspace}
    </div>
  );
}
